import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiDimensionRef, biCaseLabels } from '@euler/model/bi/query';
import { BiFieldType } from '@euler/model/bi/result';
import { Plus, X } from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';
import { BiButton } from '../../../components/ui/BiButton';
import { BiChip } from '../../../components/ui/BiChip';
import {
  BiBand,
  biBandedDimension,
  biBandsOf,
  biCaseOfBands,
  kBiMaxBandLabel,
  kBiMaxBands,
} from '../functions/biEditorCase';
import { StyleTextInput } from '../style/StyleTextInput';
import { BandBoundInput } from './BandBoundInput';

/**
 * **分段** on a numeric dimension: name the rows below each bound, in
 * order, and the rest. A case the form did not write (a template's) shows
 * its bands and can be cleared, not edited.
 */
export const DimensionCaseSection = memo(
  ({
    dimension,
    catalog,
    onChange,
  }: {
    dimension: BiDimensionRef;
    catalog: BiDatasetCatalog;
    onChange: (next: BiDimensionRef) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const field = catalog.fields.find(entry => entry.key === dimension.field);
    if (field?.type !== BiFieldType.Number) return null;
    const cased = dimension.case;
    const label = biText(field.label);
    if (!cased)
      return (
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
        >
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {label}
          </Typography.Text>
          <BiChip
            label="分段"
            selected={false}
            onPress={() => onChange(biBandedDimension(dimension.field))}
          />
        </View>
      );
    const clear = (
      <BiChip
        label="分段"
        selected
        onPress={() => onChange({ field: dimension.field })}
      />
    );
    const bands = biBandsOf(cased);
    if (!bands)
      return (
        <View style={{ gap: rpx(6) }}>
          <View
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
          >
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              {label}
            </Typography.Text>
            {clear}
          </View>
          <Typography.Text size={rpx(12)} color={aiColors.foreground}>
            {biCaseLabels(cased).join(' · ')}
          </Typography.Text>
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            这个分段不是按上限分的，取消分段后可以重新设置
          </Typography.Text>
        </View>
      );
    const rest = cased.else ?? '';
    const write = (next: BiBand[], nextRest = rest) =>
      onChange({ ...dimension, case: biCaseOfBands(next, nextRest) });
    const last = bands[bands.length - 1]?.below;
    return (
      <View style={{ gap: rpx(8) }}>
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
        >
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {label}
          </Typography.Text>
          {clear}
        </View>
        {bands.map((band, index) => (
          <View
            key={index}
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
          >
            <StyleTextInput
              label={`第 ${index + 1} 段名称`}
              value={band.label}
              placeholder="段名，如 2万公里内"
              maxLength={kBiMaxBandLabel}
              onChange={text =>
                write(
                  bands.map((entry, at) =>
                    at === index ? { ...entry, label: text } : entry,
                  ),
                )
              }
            />
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              小于
            </Typography.Text>
            <BandBoundInput
              label={`第 ${index + 1} 段上限`}
              value={band.below}
              onChange={below =>
                write(
                  bands.map((entry, at) =>
                    at === index ? { ...entry, below } : entry,
                  ),
                )
              }
            />
            <BiButton
              icon={X}
              title="删除"
              tone="quiet"
              onPress={() => write(bands.filter((_, at) => at !== index))}
            />
          </View>
        ))}
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
        >
          <StyleTextInput
            label="其余各行的名称"
            value={rest}
            placeholder="其余，如 超16万公里"
            maxLength={kBiMaxBandLabel}
            onChange={text => write(bands, text)}
          />
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {Number.isFinite(last) ? `不小于 ${last}` : '其余'}
          </Typography.Text>
        </View>
        <View style={{ alignItems: 'flex-start' }}>
          <BiButton
            icon={Plus}
            title="添加一段"
            disabled={bands.length >= kBiMaxBands}
            onPress={() => write([...bands, { label: '', below: NaN }])}
          />
        </View>
      </View>
    );
  },
);
